/**
 * Error normalization for the react-query data layer.
 *
 * WHY THIS EXISTS
 * ---------------
 * `apiClient` (shared/utils/apiClient.js) rejects failed requests with an
 * ENHANCED PLAIN OBJECT:
 *
 *     { ...axiosError, isApiError: true, status, data }
 *
 * It is NOT an `Error` instance and its `.message` is the raw axios message
 * ("Request failed with status code 403"), not the backend's human reason.
 * The service classes (profileService, redemptionService, ...) already
 * unwrap that into a real `Error` with a friendly `.message`, so query hooks
 * that call a service get a good error for free.
 *
 * This helper is the SAFETY NET for the cases a service doesn't fully cover,
 * and the single documented place the data layer reads an error shape. It is
 * deliberately additive: we do NOT modify apiClient (owned by another agent).
 *
 * CONTRACT for every query/mutation hook: pass the caught value through
 * `normalizeQueryError` (directly, or implicitly because your service already
 * throws a real Error). Components then read `error.message` for display and
 * `error.status` / `error.code` for branching, uniformly, everywhere.
 */

/**
 * Coerce any thrown/rejected value into a real Error with a usable message,
 * a numeric `status`, and a stable string `code`.
 *
 * @param {unknown} err - Whatever a service / apiClient rejected with.
 * @param {string} [fallbackMessage] - Used when no better message is found.
 * @returns {Error & { status?: number, code?: string }}
 */
export function normalizeQueryError(
  err,
  fallbackMessage = "Something went wrong",
) {
  // Already a real Error from a service class - keep it, just ensure status.
  if (err instanceof Error) {
    if (err.status == null) {
      err.status = err.response?.status ?? err.status;
    }
    return err;
  }

  const status = err?.status ?? err?.response?.status;
  const data = err?.data ?? err?.response?.data;
  const message =
    data?.message || data?.error || err?.message || fallbackMessage;

  const normalized = new Error(message);
  normalized.status = status;
  normalized.code = data?.errorCode || data?.code || statusToCode(status);
  // Preserve the original for logging/telemetry without leaking it to the UI.
  normalized.cause = err;
  return normalized;
}

function statusToCode(status) {
  switch (status) {
    case 400:
      return "BAD_REQUEST";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 422:
      return "VALIDATION_ERROR";
    case 429:
      return "RATE_LIMITED";
    default:
      if (typeof status === "number" && status >= 500) return "SERVER_ERROR";
      return "UNKNOWN_ERROR";
  }
}

export default normalizeQueryError;
